import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'
import { v4 as uuid } from 'uuid'
import {
  type BasePuiDialogProps,
  type Nil,
  PuiDialog,
} from '@pbt/pbt-ui-components'

import { LandingType } from '~/constants/landingConstants'
import { DraftDiagnosisStatusId } from '~/constants/medicalHistory'
import SnapshotsAliasTypes from '~/constants/SnapshotsAliasTypes'
import {
  createDraftDiagnoses,
  editDiagnosisHistory,
  saveDiagnosisHistory,
} from '~/store/actions/medicalHistory'
import { fetchWidgetsData } from '~/store/duck/landing'
import {
  getDiagnosesIsLoading,
  getDiffDxSearchResults,
  searchDiagnoses,
} from '~/store/duck/soapDiagnoses'
import {
  getMedicalHistoryIsFetching,
  getMedicalHistoryIsSending,
} from '~/store/reducers/medicalHistory'
import type { DiagnoseHistoryItem } from '~/types'
import { composeSearchResultsWithSelectedItems } from '~/utils/diagnosesUtils'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'

import DiagnosisHistory from './DiagnosisHistory'
import DiagnosisListWithFilters, {
  type DiagnosisSelectableListHandle,
} from './DiagnosisListWithFilters'

const useStyles = makeStyles(
  (theme) => ({
    paper: {
      width: 976,
      maxWidth: 976,
      [theme.breakpoints.down('md')]: {
        margin: '48px auto !important',
      },
      [theme.breakpoints.down('sm')]: {
        width: 'calc(100% - 32px)',
      },
    },
  }),
  { name: 'DiagnosisHistoryDialog' },
)

interface DiagnosisHistoryDialogProps extends BasePuiDialogProps {
  clientId: string | Nil
  diagnosis?: DiagnoseHistoryItem | Nil
  isForPmr?: boolean
  patientId: string | Nil
}

const DiagnosisHistoryDialog = ({
  diagnosis,
  patientId,
  clientId,
  open,
  onClose,
  isForPmr = false,
}: DiagnosisHistoryDialogProps) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const isSending = useSelector(getMedicalHistoryIsSending)
  const isFetching = useSelector(getMedicalHistoryIsFetching)
  const searchResults = useSelector(getDiffDxSearchResults)
  const isLoading = useSelector(getDiagnosesIsLoading)
  const { t } = useTranslation(['Common', 'Dialogs', 'Search'])

  const listRef = useRef<DiagnosisSelectableListHandle>(null)

  const refetchDiagnoses = () => {
    dispatch(
      fetchWidgetsData([SnapshotsAliasTypes.Diagnoses], {
        quiet: false,
        landingType: LandingType.CLIENT_AND_PATIENT_SNAPSHOTS,
        patientId,
      }),
    )
  }

  const closeAfterUpdate = () => {
    refetchDiagnoses()
    if (onClose) {
      onClose()
    }
  }

  const setCloseAfterCreationOn = useCloseAfterCreation(
    closeAfterUpdate,
    getMedicalHistoryIsSending,
  )

  const onProceedForPmr = (items: DiagnoseHistoryItem[]) => {
    if (patientId) {
      const itemsToSave = items.map((item) => ({
        ...item,
        id: uuid(),
        statusId: DraftDiagnosisStatusId,
        descriptionTranslation: item.name,
      }))
      dispatch(createDraftDiagnoses(itemsToSave, patientId))
      onClose?.()
    }
  }

  const onProceed = (items: DiagnoseHistoryItem[]) => {
    setCloseAfterCreationOn()
    if (patientId) {
      if (diagnosis) {
        dispatch(editDiagnosisHistory(items, patientId))
      } else {
        dispatch(
          saveDiagnosisHistory(
            R.map(R.omit(['id']), items),
            patientId,
            clientId,
          ),
        )
      }
    }
  }

  const [searchResultsWithUpdatedFields, setSearchResultsWithUpdatedFields] =
    useState(searchResults)

  useEffect(() => {
    if (searchResults && listRef.current) {
      const selectedItems = listRef.current?.getItems()
      const updatedItems = composeSearchResultsWithSelectedItems(
        searchResults,
        selectedItems,
      )
      setSearchResultsWithUpdatedFields(updatedItems)
    } else {
      setSearchResultsWithUpdatedFields(searchResults)
    }
  }, [searchResults])

  return (
    <PuiDialog
      aria-labelledby="diagnosis-history-dialog"
      classes={{
        paper: classes.paper,
      }}
      open={open}
      title={
        diagnosis
          ? t('Dialogs:DIAGNOSIS_HISTORY_DIALOG.TITLE_EDIT')
          : t('Common:ADD_DIAGNOSIS_HISTORY')
      }
      onClose={onClose}
    >
      <Grid container direction="column" p={2}>
        {diagnosis ? (
          <DiagnosisHistory diagnosis={diagnosis} onProceed={onProceed} />
        ) : (
          <DiagnosisListWithFilters
            isLoading={isSending}
            isReceivingListItems={isFetching || isLoading}
            patientId={patientId}
            proceedButtonText={
              isForPmr ? t('Common:ADD_TO_MEDICAL_RECORD') : undefined
            }
            ref={listRef}
            searchItems={({ searchTerm }) =>
              dispatch(searchDiagnoses(searchTerm, patientId))
            }
            searchPlaceholder={t('Search:DIFFERENTIAL_DIAGNOSES')}
            searchResults={searchResultsWithUpdatedFields}
            onProceed={isForPmr ? onProceedForPmr : onProceed}
          />
        )}
      </Grid>
    </PuiDialog>
  )
}

export default DiagnosisHistoryDialog
